import { detailOr } from "@/lib/apiErrorMessage";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, ShieldAlert, ExternalLink, History } from "lucide-react";
import i18n from "@/i18n";

const LABELS = {
    get msa() { return i18n.t("admin.doc.msa", "Master Service Agreement"); },
    get tos() { return i18n.t("admin.doc.tos", "Terms of Service"); },
    get privacy() { return i18n.t("admin.doc.privacy", "Privacy Policy"); },
    get dpa() { return i18n.t("admin.doc.dpa", "Data Processing Agreement"); },
    get sla() { return i18n.t("admin.doc.sla", "Service Level Agreement"); },
    get cookie() { return i18n.t("admin.doc.cookie", "Cookie Policy"); },
};

/**
 * CompanyLegalStatusPanel — Super-Admin read-only status view.
 * Rendered inside Company Administration.
 *
 * Shows, per currently-active legal document:
 *   • whether the company has an active acceptance,
 *   • which version they accepted (compared against the current one),
 *   • who accepted and when,
 *   • a link to the public document.
 *
 * Also surfaces:
 *   • `legacy_customer` — company predates the electronic gate.
 *   • Full acceptance history (append-only) with unredacted IPs.
 *   • Any decline events (rare — recorded when an eligible admin
 *     refused the wizard and was logged out).
 *
 * Management actions (Publish new version, Force re-acceptance, etc.)
 * are OUT OF SCOPE for this iteration but the panel is designed to
 * host them later without a redesign.
 */
export default function CompanyLegalStatusPanel({ companyId }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [active, setActive] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [status, docs] = await Promise.all([
                    api.get(`/admin/companies/${companyId}/legal`),
                    api.get("/admin/legal/documents"),
                ]);
                if (cancelled) return;
                setData(status.data);
                setActive((docs.data.documents || []).filter((d) => d.is_active));
            } catch (e) {
                if (!cancelled) setError(detailOr(e?.response?.data?.detail, i18n.t("admin.l.Could_not_load_legal_status", "Could not load legal status")));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [companyId]);

    if (loading) {
        return <div className="text-sm text-neutral-500" data-testid="legal-status-loading">{i18n.t("admin.l.Loading_legal_status", "Loading legal status…")}</div>;
    }
    if (error) {
        return <div className="text-sm text-red-600" data-testid="legal-status-error">{error}</div>;
    }
    if (!data) return null;

    const current = data.legal_current || {};
    const isLegacy = !!data.legacy_customer;

    return (
        <div data-testid="legal-status-panel" className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-neutral-700" />
                    <h3 className="text-base font-display font-bold">{i18n.t("admin.l.Legal_Status", "Legal Status")}</h3>
                </div>
                {isLegacy && (
                    <span data-testid="legal-status-legacy-badge"
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 text-[11px] font-semibold px-2 py-0.5">
                        <ShieldAlert className="h-3 w-3" />
                        {i18n.t("admin.l.Legacy_customer_no_electronic_acceptance_on_file", "Legacy customer — no electronic acceptance on file")}
                    </span>
                )}
            </div>

            {active.length === 0 && (
                <div className="text-sm text-neutral-500">
                    {i18n.t("admin.l.No_active_legal_documents_configured", "No active legal documents configured.")}
                </div>
            )}

            <div className="rounded-md border border-neutral-200 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-[11px] uppercase tracking-wide text-neutral-500">
                        <tr>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Document", "Document")}</th>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Current", "Current")}</th>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Accepted", "Accepted")}</th>
                            <th className="text-left px-3 py-2">By</th>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Status", "Status")}</th>
                            <th className="text-right px-3 py-2">{i18n.t("admin.l.Link", "Link")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {active.map((doc) => {
                            const acc = current[doc.document_type];
                            const upToDate = acc && acc.version && acc.version === doc.version;
                            const label = LABELS[doc.document_type] || doc.document_type.toUpperCase();
                            return (
                                <tr key={doc.id} data-testid={`legal-status-row-${doc.document_type}`}>
                                    <td className="px-3 py-2 font-medium">{label}
                                        {!doc.is_required && (
                                            <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-neutral-500">{i18n.t("admin.l.informational", "informational")}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">v{doc.version}</td>
                                    <td className="px-3 py-2 text-xs">
                                        {acc ? <>v{acc.version} · {new Date(acc.accepted_at).toLocaleString()}</> : <span className="text-neutral-400">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {acc?.accepting_user_id ? acc.accepting_user_id.slice(0, 8) + "…" : <span className="text-neutral-400">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {doc.is_required
                                            ? (upToDate
                                                ? <span className="text-emerald-700 font-semibold">{i18n.t("admin.l.Accepted", "Accepted")}</span>
                                                : (acc
                                                    ? <span className="text-amber-700 font-semibold">{i18n.t("admin.l.Stale", "Stale")}</span>
                                                    : (isLegacy
                                                        ? <span className="text-neutral-500">{i18n.t("admin.l.Legacy", "Legacy")}</span>
                                                        : <span className="text-red-700 font-semibold">{i18n.t("admin.l.Missing", "Missing")}</span>)))
                                            : <span className="text-neutral-500">n/a</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <a href={doc.url} target="_blank" rel="noopener noreferrer"
                                           className="inline-flex items-center gap-1 text-xs underline">
                                            Open <ExternalLink className="h-3 w-3" />
                                        </a>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <details className="rounded-md border border-neutral-200"
                     data-testid="legal-status-history">
                <summary className="cursor-pointer px-3 py-2 text-sm font-semibold bg-neutral-50 flex items-center gap-1.5">
                    <History className="h-4 w-4" /> Acceptance history ({(data.acceptances || []).length})
                </summary>
                <div className="p-3 space-y-2 text-xs">
                    {(data.acceptances || []).length === 0
                        ? <div className="text-neutral-500">{i18n.t("admin.noAcceptance", "No acceptance history")}{isLegacy ? i18n.t("admin.legacySuffix", " — legacy customer") : ""}.</div>
                        : (
                            <ul className="space-y-1">
                                {data.acceptances.map((a) => (
                                    <li key={a.id} className="font-mono flex flex-wrap gap-x-3">
                                        <span className={a.is_active ? "text-emerald-700" : "text-neutral-500 line-through"}>
                                            {a.document_type} v{a.document_version}
                                        </span>
                                        <span className="text-neutral-500">{a.accepted_at}</span>
                                        <span className="text-neutral-700">{a.accepting_user_email}</span>
                                        <span className="text-neutral-500">{a.ip_address}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    {(data.declines || []).length > 0 && (
                        <div className="pt-2 border-t border-neutral-200 mt-2">
                            <div className="font-semibold mb-1">Declines ({data.declines.length})</div>
                            <ul className="space-y-1">
                                {data.declines.map((d) => (
                                    <li key={d.id} className="font-mono text-neutral-500">
                                        {d.declined_at} · {d.declining_user_email}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </details>
        </div>
    );
}
